import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * useAsyncTasks — V23.15.45
 *
 * Poll les tâches async de l'utilisateur toutes les 6 secondes.
 * Affiche automatiquement un toast quand une tâche passe running→done/failed.
 * Marque la tâche comme notifiée côté serveur (pour ne pas re-notifier).
 *
 * Retourne { running_count, running_tasks } pour affichage éventuel dans un badge.
 */
const POLL_MS = 6000;

export default function useAsyncTasks({ enabled = true } = {}) {
  const [state, setState] = useState({ running_count: 0, running_tasks: [] });
  const knownRunningIds = useRef(new Set());
  const alive = useRef(true);

  const markSeen = useCallback(async (taskId) => {
    try {
      await api.post(`/workspace/async-tasks/mark-seen/${taskId}`);
    } catch { /* silent */ }
  }, []);

  const tick = useCallback(async () => {
    if (!alive.current) return;
    try {
      const r = await api.get("/workspace/async-tasks/mine");
      const running = r.data?.running || [];
      const doneToNotify = r.data?.done_pending_notification || [];
      const failedToNotify = r.data?.failed_pending_notification || [];

      // Update running set
      const newRunningIds = new Set(running.map((t) => t.id));
      knownRunningIds.current = newRunningIds;

      // Fire done toasts
      for (const task of doneToNotify) {
        toast.success(`✓ ${task.label} · prêt`, {
          description: task.result_route ? "Ouvre pour consulter le résultat." : "Consulte le module concerné.",
          duration: 8000,
          action: task.result_route
            ? {
                label: "Ouvrir →",
                onClick: () => {
                  window.location.assign(task.result_route);
                },
              }
            : undefined,
        });
        markSeen(task.id);
      }
      // Fire failed toasts
      for (const task of failedToNotify) {
        toast.error(`✗ ${task.label} · échec`, {
          description: task.error || "Une erreur s'est produite.",
          duration: 10000,
        });
        markSeen(task.id);
      }

      setState({ running_count: running.length, running_tasks: running });
    } catch { /* silent */ }
  }, [markSeen]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return undefined;
    tick(); // immediate
    const iv = setInterval(tick, POLL_MS);
    return () => {
      alive.current = false;
      clearInterval(iv);
    };
  }, [enabled, tick]);

  return state;
}

/**
 * AsyncTasksBadge — badge visible dans le top bar.
 * Affiche le nombre de tâches en cours + tooltip listant les labels.
 */
export function AsyncTasksBadge() {
  const { running_count, running_tasks } = useAsyncTasks({ enabled: true });
  if (!running_count) return null;
  return (
    <Link
      to="#"
      onClick={(e) => e.preventDefault()}
      className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75] text-[10px] font-semibold group relative"
      data-testid="async-tasks-badge"
      title={running_tasks.map((t) => t.label).join(" · ")}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-[#1D9E75] animate-pulse" />
      {running_count} en cours
      {/* Tooltip on hover */}
      <div className="absolute top-full right-0 mt-2 min-w-[220px] bg-zinc-950 border border-white/10 rounded-md p-2 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50 shadow-lg">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-1.5">Analyses en cours</div>
        {running_tasks.map((t) => (
          <div key={t.id} className="text-xs text-zinc-200 py-1 flex items-start gap-2">
            <span className="w-1 h-1 mt-1.5 rounded-full bg-[#1D9E75] shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              <div className="truncate">{t.label}</div>
              <div className="text-[9px] text-zinc-500">{t.progress_hint || "En cours…"}</div>
            </div>
          </div>
        ))}
      </div>
    </Link>
  );
}
